package fr.manyverse.app.presentation.components.modules.lockpicking

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.domain.validation.LockpickingValidator
import fr.manyverse.app.presentation.components.SwitchRow
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelectionMode
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelector
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.lockpicking_launch
import manyverse.shared.generated.resources.lockpicking_launch_title
import manyverse.shared.generated.resources.lockpicking_lock_count_label
import manyverse.shared.generated.resources.lockpicking_show_lock_count
import manyverse.shared.generated.resources.lockpicking_sound_enabled
import manyverse.shared.generated.resources.lockpicking_time_limit_enabled
import manyverse.shared.generated.resources.lockpicking_time_limit_label
import org.jetbrains.compose.resources.stringResource

/**
 * Dialog for the GM to configure and launch a lockpicking challenge.
 */
@Composable
fun LockpickingCreationDialog(
    showDialog: Boolean,
    players: List<TableMember>,
    labelOf: (TableMember) -> String = { it.userId.take(8) },
    isLoading: Boolean,
    errorMessage: String?,
    onDismiss: () -> Unit,
    onConfirm: (
        targetParticipantId: String,
        lockCount: Int,
        timeLimitSeconds: Int?,
        showLockCount: Boolean,
        soundEnabled: Boolean
    ) -> Unit,
    modifier: Modifier = Modifier
) {
    var selectedPlayerId by remember(showDialog) { mutableStateOf("") }
    var lockCount by remember(showDialog) { mutableFloatStateOf(2f) }
    var timeLimitEnabled by remember(showDialog) { mutableStateOf(false) }
    var timeLimitSlider by remember(showDialog) { mutableFloatStateOf(60f) }
    var showLockCount by remember(showDialog) { mutableStateOf(false) }
    var soundEnabled by remember(showDialog) { mutableStateOf(true) }

    val lockCountInt = lockCount.toInt()
    val timeLimitSeconds = if (timeLimitEnabled) (timeLimitSlider / 30).toInt() * 30 else null

    val canSubmit = selectedPlayerId.isNotBlank() && !isLoading

    if (!showDialog) return

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.lockpicking_launch_title)) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Target player selection (single)
                ParticipantSelector(
                    players = players,
                    mode = ParticipantSelectionMode.Single(
                        selectedId = selectedPlayerId,
                        onSelect = { selectedPlayerId = it }
                    ),
                    labelOf = labelOf,
                    isEnabled = !isLoading
                )

                // Lock count slider
                Column {
                    Text(
                        text = stringResource(Res.string.lockpicking_lock_count_label, lockCountInt),
                        style = MaterialTheme.typography.labelMedium
                    )
                    Slider(
                        value = lockCount,
                        onValueChange = { lockCount = it },
                        valueRange = LockpickingValidator.MIN_LOCK_COUNT.toFloat()..LockpickingValidator.MAX_LOCK_COUNT.toFloat(),
                        steps = LockpickingValidator.MAX_LOCK_COUNT - LockpickingValidator.MIN_LOCK_COUNT - 1,
                        enabled = !isLoading
                    )
                }

                // Time limit toggle + slider
                SwitchRow(
                    label = stringResource(Res.string.lockpicking_time_limit_enabled),
                    checked = timeLimitEnabled,
                    onCheckedChange = { timeLimitEnabled = it },
                    enabled = !isLoading
                )
                if (timeLimitEnabled) {
                    val timeLimitInt = (timeLimitSlider / 30).toInt() * 30
                    Column {
                        Text(
                            text = stringResource(Res.string.lockpicking_time_limit_label, timeLimitInt),
                            style = MaterialTheme.typography.labelMedium
                        )
                        Slider(
                            value = timeLimitSlider,
                            onValueChange = { timeLimitSlider = it },
                            valueRange = LockpickingValidator.MIN_TIME_LIMIT_SECONDS.toFloat()..LockpickingValidator.MAX_TIME_LIMIT_SECONDS.toFloat(),
                            steps = ((LockpickingValidator.MAX_TIME_LIMIT_SECONDS - LockpickingValidator.MIN_TIME_LIMIT_SECONDS) / 30) - 1,
                            enabled = !isLoading
                        )
                    }
                }

                // Show lock count toggle
                SwitchRow(
                    label = stringResource(Res.string.lockpicking_show_lock_count),
                    checked = showLockCount,
                    onCheckedChange = { showLockCount = it },
                    enabled = !isLoading
                )

                // Sound toggle
                SwitchRow(
                    label = stringResource(Res.string.lockpicking_sound_enabled),
                    checked = soundEnabled,
                    onCheckedChange = { soundEnabled = it },
                    enabled = !isLoading
                )

                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(
                    onClick = onDismiss,
                    enabled = !isLoading
                ) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = {
                        onConfirm(
                            selectedPlayerId,
                            lockCountInt,
                            timeLimitSeconds,
                            showLockCount,
                            soundEnabled
                        )
                    },
                    enabled = canSubmit
                ) {
                    Text(stringResource(Res.string.lockpicking_launch))
                }
            }
        },
        modifier = modifier
    )
}
